import { Button } from 'semantic-ui-react';
import styled from '@emotion/styled';

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

const ActiveStatusBadge = styled(StatusBadge)`
  background-color: #d4edda;
  color: #155724;
`;

const InactiveStatusBadge = styled(StatusBadge)`
  background-color: #f8d7da;
  color: #721c24;
`;

const DifficultyBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

const BeginnerBadge = styled(DifficultyBadge)`
  background-color: #28a74520;
  color: #28a745;
`;

const IntermediateBadge = styled(DifficultyBadge)`
  background-color: #ffc10720;
  color: #ffc107;
`;

const AdvancedBadge = styled(DifficultyBadge)`
  background-color: #dc354520;
  color: #dc3545;
`;

const CategoryBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

const IntroductionBadge = styled(CategoryBadge)`
  background-color: #007bff20;
  color: #007bff;
`;

const TheoryBadge = styled(CategoryBadge)`
  background-color: #6f42c120;
  color: #6f42c1;
`;

const PracticeBadge = styled(CategoryBadge)`
  background-color: #28a74520;
  color: #28a745;
`;

const AssessmentBadge = styled(CategoryBadge)`
  background-color: #fd7e1420;
  color: #fd7e14;
`;

const ResourcesBadge = styled(CategoryBadge)`
  background-color: #20c99720;
  color: #20c997;
`;

const DefaultBadge = styled(CategoryBadge)`
  background-color: #6c757d20;
  color: #6c757d;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled(Button)`
  && {
    margin: 0;
    height: 32px;
    padding: 0 8px;
    min-width: auto;

    &.edit-button {
      background-color: #007bff !important;
      color: white !important;

      &:hover {
        background-color: #0056b3 !important;
      }
    }

    &.view-button {
      background-color: #28a745 !important;
      color: white !important;

      &:hover {
        background-color: #218838 !important;
      }
    }

    &.create-button {
      background-color: #fd7e14 !important;
      color: white !important;

      &:hover {
        background-color: #e8690b !important;
      }
    }

    &.delete-button {
      background-color: #dc3545 !important;
      color: white !important;

      &:hover {
        background-color: #c82333 !important;
      }
    }
  }
`;

// Course column definitions for AG Grid
export const getCourseColumns = (
  handleEditCourse,
  handleDelete,
  handleViewLessons,
  handleCreateLessonForCourse
) => [
  {
    headerName: 'Título',
    field: 'title',
    sortable: true,
    filter: true,
    flex: 2,
    minWidth: 200,
  },
  {
    headerName: 'Descripción',
    field: 'description',
    sortable: true,
    filter: true,
    flex: 3,
    minWidth: 300,
    cellRenderer: (params) => {
      const description = params.data.description || '';
      return description.length > 100 ? `${description.substring(0, 100)}...` : description;
    },
  },
  {
    headerName: 'Lecciones',
    field: 'lessonCount',
    sortable: true,
    filter: 'agNumberColumnFilter',
    flex: 1,
    minWidth: 100,
    valueGetter: (params) => params.data.AcademyLessons?.length || 0,
  },
  {
    headerName: 'Estado',
    field: 'status',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
    cellRenderer: (params) => {
      const isActive = params.data.status !== false;
      return isActive ? (
        <ActiveStatusBadge>Activo</ActiveStatusBadge>
      ) : (
        <InactiveStatusBadge>Inactivo</InactiveStatusBadge>
      );
    },
  },
  {
    headerName: 'Dificultad',
    field: 'difficulty',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 120,
    cellRenderer: (params) => {
      const difficulty = params.data.difficulty || 'beginner';
      switch (difficulty) {
        case 'beginner':
          return <BeginnerBadge>Principiante</BeginnerBadge>;
        case 'intermediate':
          return <IntermediateBadge>Intermedio</IntermediateBadge>;
        case 'advanced':
          return <AdvancedBadge>Avanzado</AdvancedBadge>;
        default:
          return <BeginnerBadge>Principiante</BeginnerBadge>;
      }
    },
  },
  {
    headerName: 'Duración',
    field: 'estimatedDuration',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 120,
  },
  {
    headerName: 'Acciones',
    field: 'actions',
    sortable: false,
    filter: false,
    flex: 1,
    minWidth: 200,
    cellRenderer: (params) => (
      <ActionButtonsContainer>
        <ActionButton
          icon="edit"
          size="mini"
          className="edit-button"
          onClick={() => handleEditCourse(params.data)}
        />
        <ActionButton
          icon="list"
          size="mini"
          className="view-button"
          onClick={() => handleViewLessons(params.data)}
          title="Ver Lecciones"
        />
        <ActionButton
          icon="plus"
          size="mini"
          className="create-button"
          onClick={() => handleCreateLessonForCourse(params.data)}
          title="Crear Lección"
        />
        <ActionButton
          icon="trash"
          size="mini"
          className="delete-button"
          onClick={() => handleDelete(params.data, 'course')}
        />
      </ActionButtonsContainer>
    ),
  },
];

// Lesson column definitions for AG Grid
export const getLessonColumns = (
  courses,
  handleEditLesson,
  handleDelete,
  handleFullEditor,
  selectedCourse = null
) => [
  {
    headerName: 'Título',
    field: 'title',
    sortable: true,
    filter: true,
    flex: 2,
    minWidth: 200,
  },
  {
    headerName: 'Categoría',
    field: 'category',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 120,
    cellRenderer: (params) => {
      const category = params.data.category || '';
      switch (category) {
        case 'introduction':
          return <IntroductionBadge>Introducción</IntroductionBadge>;
        case 'theory':
          return <TheoryBadge>Teoría</TheoryBadge>;
        case 'practice':
          return <PracticeBadge>Práctica</PracticeBadge>;
        case 'assessment':
          return <AssessmentBadge>Evaluación</AssessmentBadge>;
        case 'resources':
          return <ResourcesBadge>Recursos</ResourcesBadge>;
        default:
          return <DefaultBadge>{category || 'Sin Categoría'}</DefaultBadge>;
      }
    },
  },
  {
    headerName: 'Orden',
    field: 'order',
    sortable: true,
    filter: 'agNumberColumnFilter',
    flex: 0.8,
    minWidth: 80,
  },
  ...(selectedCourse
    ? []
    : [
        {
          headerName: 'Curso',
          field: 'courseTitle',
          sortable: true,
          filter: true,
          flex: 2,
          minWidth: 200,
          valueGetter: (params) => {
            const course = courses.find((c) =>
              c.AcademyLessons?.some((l) => l.id === params.data.id)
            );
            return course?.title || 'N/A';
          },
        },
      ]),
  {
    headerName: 'Duración',
    field: 'duration',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
  },
  {
    headerName: 'Estado',
    field: 'status',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
    cellRenderer: (params) => {
      const isActive = params.data.status !== false;
      return isActive ? (
        <ActiveStatusBadge>Activo</ActiveStatusBadge>
      ) : (
        <InactiveStatusBadge>Inactivo</InactiveStatusBadge>
      );
    },
  },
  {
    headerName: 'Bloqueado',
    field: 'isLocked',
    sortable: true,
    filter: true,
    flex: 1,
    minWidth: 100,
    cellRenderer: (params) => {
      const isLocked = params.data.isLocked || false;
      return (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: isLocked ? '#f8d7da' : '#d4edda',
            color: isLocked ? '#721c24' : '#155724',
          }}
        >
          {isLocked ? 'Sí' : 'No'}
        </span>
      );
    },
  },
  {
    headerName: 'Acciones',
    field: 'actions',
    sortable: false,
    filter: false,
    flex: 1,
    minWidth: 200,
    cellRenderer: (params) => (
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          icon="edit"
          size="mini"
          color="blue"
          onClick={() => handleEditLesson(params.data)}
          style={{ margin: 0, height: '32px', padding: '0 8px' }}
        />
        <Button
          icon="external alternate"
          size="mini"
          color="green"
          onClick={() => handleFullEditor(params.data)}
          style={{ margin: 0, height: '32px', padding: '0 8px' }}
          title="Editor Completo"
        />
        <Button
          icon="trash"
          size="mini"
          color="red"
          onClick={() => handleDelete(params.data, 'lesson')}
          style={{ margin: 0, height: '32px', padding: '0 8px' }}
        />
      </div>
    ),
  },
];
